/**
 * 浏览器内 EIP-1193 Provider 解析。
 *
 * 1) EIP-6963（Trust 官方推荐）：announceProvider + requestProvider。
 *    Trust Wallet iOS 26.20+ 在 document_start 阶段就 announce，所以必须尽早
 *    挂监听。
 * 2) 旧版兜底：ethereum.providers[]、window.ethereum、window.trustwallet、
 *    window.TrustWeb3Provider，以及定时轮询。
 *
 * 仅打印关键事件（announce / pick / wait timeout）。
 *
 * @see https://eips.ethereum.org/EIPS/eip-6963
 * @see https://developer.trustwallet.com/developer/develop-for-trust/browser-extension/evm
 */

function isEip1193Provider(p) {
  return !!(p && typeof p.request === 'function')
}

function providerFlags(p) {
  if (!p) return ''
  const flags = []
  if (p.isMetaMask) flags.push('mm')
  if (p.isTrust) flags.push('trust')
  if (p.isTrustWallet) flags.push('tw')
  if (p.isCoinbaseWallet) flags.push('cb')
  if (p.isOkxWallet) flags.push('okx')
  return flags.length ? flags.join('+') : 'unknown'
}

/** @type {Map<string, { info: object, provider: object }>} */
const eip6963Announced = new Map()
let eip6963ListenerAttached = false
let primeCalled = false

function announceProviderKey(detail) {
  const u = detail && detail.info && detail.info.uuid
  if (u) return String(u)
  const rdns = (detail && detail.info && detail.info.rdns) || ''
  const name = (detail && detail.info && detail.info.name) || ''
  return rdns + '::' + name
}

function onEip6963Announce(event) {
  try {
    const detail = event.detail
    if (!detail || !detail.info || !detail.provider) return
    const key = announceProviderKey(detail)
    if (!key || eip6963Announced.has(key)) return
    eip6963Announced.set(key, { info: detail.info, provider: detail.provider })
    console.log('[EIP-6963] announce', detail.info.rdns, detail.info.name, providerFlags(detail.provider))
  } catch (e) {
    /* no-op */
  }
}

function attachEip6963Listener() {
  if (eip6963ListenerAttached || typeof window === 'undefined') return
  eip6963ListenerAttached = true
  window.addEventListener('eip6963:announceProvider', onEip6963Announce)
  if (typeof document !== 'undefined') {
    document.addEventListener('eip6963:announceProvider', onEip6963Announce)
  }
}

function requestWalletsAnnounce() {
  if (typeof window === 'undefined') return
  try {
    window.dispatchEvent(new Event('eip6963:requestProvider'))
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new Event('eip6963:requestProvider'))
    }
  } catch (e) {
    /* no-op */
  }
}

/**
 * 入口：尽早调用以避免错过 announce。幂等。
 */
export function primeEip6963Discovery() {
  attachEip6963Listener()
  requestWalletsAnnounce()
  if (!primeCalled) {
    primeCalled = true
    // 多波 requestProvider 应对延迟 announce 的钱包
    ;[10, 50, 150, 400, 900, 2000].forEach((d) => setTimeout(requestWalletsAnnounce, d))
  }
}

function pickEip6963Provider() {
  attachEip6963Listener()
  requestWalletsAnnounce()
  const entries = [...eip6963Announced.values()]
  if (entries.length === 0) return null
  // Trust 优先（rdns 以 com.trustwallet 开头）
  const tw = entries.find((e) => /^com\.trustwallet/i.test((e.info && e.info.rdns) || ''))
  if (tw && isEip1193Provider(tw.provider)) return tw.provider
  // 否则任意一个有效的
  const any = entries.find((e) => isEip1193Provider(e.provider))
  return any ? any.provider : null
}

function pickLegacyInjectedProvider() {
  if (typeof window === 'undefined') return null
  const w = window
  const eth = w.ethereum

  if (isEip1193Provider(eth) && Array.isArray(eth.providers) && eth.providers.length > 0) {
    const trust = eth.providers.find((p) => p && (p.isTrust === true || p.isTrustWallet === true))
    if (isEip1193Provider(trust)) return trust
    const mm = eth.providers.find((p) => p && p.isMetaMask === true)
    if (isEip1193Provider(mm)) return mm
    const cb = eth.providers.find((p) => p && p.isCoinbaseWallet === true)
    if (isEip1193Provider(cb)) return cb
    const first = eth.providers.find((p) => isEip1193Provider(p))
    if (first) return first
  }
  if (isEip1193Provider(eth)) return eth

  const tw = w.trustwallet || w.trustWallet
  if (tw && typeof tw === 'object') {
    if (isEip1193Provider(tw.ethereum)) return tw.ethereum
    if (isEip1193Provider(tw)) return tw
  }
  if (isEip1193Provider(w.TrustWeb3Provider)) return w.TrustWeb3Provider

  return null
}

/**
 * 同步获取浏览器注入的 EIP-1193 Provider。
 */
export function getBrowserEthereumProvider() {
  const fromEip6963 = pickEip6963Provider()
  if (fromEip6963) return fromEip6963
  return pickLegacyInjectedProvider()
}

export function hasBrowserEthereumProvider() {
  return getBrowserEthereumProvider() != null
}

/**
 * 等待注入式 provider 就绪（应对 EIP-6963 异步 announce、晚到的 ethereum 注入）。
 * 不会弹任何 UI。
 */
export async function waitForBrowserEthereumProvider({ timeoutMs = 3000, intervalMs = 120 } = {}) {
  let p = getBrowserEthereumProvider()
  if (p) return p
  const start = Date.now()
  const deadline = start + timeoutMs
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, intervalMs))
    p = getBrowserEthereumProvider()
    if (p) {
      console.log('[EIP-6963] provider ready after', Date.now() - start, 'ms', providerFlags(p))
      return p
    }
  }
  console.warn('[EIP-6963] wait timeout', timeoutMs, 'ms; eip6963Count=', eip6963Announced.size)
  return null
}
